import { useEffect, useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";
import { flushSync } from "react-dom";

/**
 * Motion that CSS can't do on its own (DSPRT-201). Each helper here works on
 * markup the app already renders, so no call site has to opt in. Every one of
 * them does nothing under prefers-reduced-motion. The global rule in
 * responsive.css already cuts CSS motion to an instant; these helpers just
 * don't start.
 *
 * Timings are the motion tokens in tokens.css, repeated as numbers because
 * the Web Animations API takes milliseconds, not custom properties.
 */

export const EASE_OUT = "cubic-bezier(.22, 1, .36, 1)";
export const EASE_IN = "cubic-bezier(.4, 0, 1, 1)";
export const EASE_IN_OUT = "cubic-bezier(.65, 0, .35, 1)";
export const EASE_SPRING = "cubic-bezier(.34, 1.4, .64, 1)";

const reduceQuery = typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
export function reducedMotion(): boolean {
  return !!reduceQuery?.matches;
}

/* ------------------------------------------------------------------ *
 * 1. Page transitions
 * ------------------------------------------------------------------ */

type ViewTransition = { ready: Promise<void>; finished: Promise<void>; updateCallbackDone: Promise<void> };
type ViewTransitionDocument = Document & { startViewTransition?: (cb: () => void) => ViewTransition };

/**
 * Runs a route change inside a View Transition, so the old page fades out and
 * the new one rises in (the keyframes are in base.css). flushSync makes React
 * commit the new page inside the callback, which is the moment the browser
 * takes its "after" picture. Without View Transitions, or with reduced motion,
 * the change happens at once, as it always did.
 */
export function pageTransition(update: () => void, direction = 0): void {
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition || reducedMotion() || document.visibilityState !== "visible") {
    update();
    return;
  }
  // Which way the page slides (DSPRT-203): toward a section further along
  // the nav it comes in from the right, back toward the start from the left.
  // Back and Forward follow the same order, so Back undoes the way you came.
  const html = document.documentElement;
  html.dataset.vt = direction > 0 ? "next" : direction < 0 ? "prev" : "page";
  const t = doc.startViewTransition(() => flushSync(update));
  // A transition the browser skips (the tab went to the background, another
  // one started) rejects these; the page has already changed, so that's fine.
  t.ready.catch(() => {});
  t.finished.catch(() => {}).finally(() => { if (html.dataset.vt !== "theme") delete html.dataset.vt; });
}

/**
 * The theme switch: one View Transition that crossfades the old picture into
 * the new one. (DSPRT-203 first grew the new theme as a hard-edged circle from
 * the button; the edge and the frozen snapshot read as a jolt, so it is a plain
 * fade now.) The header and tab bar are left out of the page-transition naming
 * for it, so they fade with everything else. `from` is kept for the callers.
 */
export function themeWipe(_from: Element | null | undefined, update: () => void): void {
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition || reducedMotion() || document.visibilityState !== "visible") {
    update();
    return;
  }
  const html = document.documentElement;
  html.dataset.vt = "theme";
  const t = doc.startViewTransition(() => flushSync(update));
  t.finished.catch(() => {}).finally(() => { delete html.dataset.vt; });
}

/* ------------------------------------------------------------------ *
 * 8. Favouriting gives a small burst (DSPRT-203)
 * ------------------------------------------------------------------ */

/** The star pops and eight sparks fly out from it. Called once a favourite is saved. */
export function favBurst(btn: HTMLElement | null): void {
  if (!btn || reducedMotion()) return;
  const icon = btn.querySelector<SVGElement>(".icon");
  if (!icon) return;
  icon.animate(
    [{ transform: "scale(.5)" }, { transform: "scale(1.3)", offset: .55 }, { transform: "scale(1)" }],
    { duration: 450, easing: EASE_SPRING },
  );
  const b = btn.getBoundingClientRect(), i = icon.getBoundingClientRect();
  const cx = i.left - b.left + i.width / 2, cy = i.top - b.top + i.height / 2;
  for (let k = 0; k < 8; k++) {
    const s = document.createElement("i");
    s.className = "favspark";
    s.setAttribute("aria-hidden", "true");
    Object.assign(s.style, { left: `${cx}px`, top: `${cy}px` });
    btn.appendChild(s);
    const ang = (k / 8) * Math.PI * 2;
    const a = s.animate(
      [{ transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
       { transform: `translate(calc(-50% + ${Math.cos(ang) * 17}px), calc(-50% + ${Math.sin(ang) * 17}px)) scale(.3)`, opacity: 0 }],
      { duration: 500, delay: 60, easing: EASE_OUT, fill: "both" },
    );
    const end = () => s.remove();
    a.onfinish = end;
    a.oncancel = end;
  }
}

/* ------------------------------------------------------------------ *
 * 8. The selection slides
 * ------------------------------------------------------------------ */

type SlideKind = "pill" | "line" | "bar" | "under" | "seg";
const SLIDERS: ReadonlyArray<{ group: string; item: string; kind: SlideKind }> = [
  { group: ".toggle", item: ".toggle__btn", kind: "pill" },
  // The masthead's segmented switches (DSPRT-280): the filled segment slides along its track.
  { group: ".seg", item: ".seg__btn", kind: "seg" },
  { group: ".topnav", item: ".topnav__link", kind: "line" },
  { group: ".tabbar__list", item: ".tabbar__item", kind: "bar" },
  // The phone bracket's round tabs (DSPRT-205): a full-width underline.
  { group: ".swb__tabs", item: ".swb__tabs > button", kind: "under" },
];
const running = new WeakMap<Element, Animation>();

function hasOn(classes: string | null): boolean {
  return !!classes && classes.split(/\s+/).includes("is-on");
}

/**
 * Where the moving mark sits on one item, relative to its group. A pill covers
 * the whole button; a line is the topnav underline (inset by the link's own
 * side padding, flush with the bar's bottom rule); a bar is the 28px marker on
 * top of a tab bar item.
 */
function markBox(kind: SlideKind, item: HTMLElement, group: HTMLElement) {
  const g = group.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  const left = r.left - g.left + group.scrollLeft;
  const top = r.top - g.top + group.scrollTop;
  if (kind === "pill" || kind === "seg") return { left, top, width: r.width, height: r.height };
  if (kind === "line") {
    const pad = parseFloat(getComputedStyle(item).paddingLeft) || 0;
    return { left: left + pad, top: top + r.height - 1, width: r.width - pad * 2, height: 2 };
  }
  if (kind === "under") return { left, top: top + r.height - 2, width: r.width, height: 2 };
  return { left: left + r.width / 2 - 14, top, width: 28, height: 2.5 };
}

function slide(kind: SlideKind, group: HTMLElement, from: HTMLElement, to: HTMLElement) {
  running.get(group)?.finish();
  // A group that isn't showing (the tab bar on desktop) has nothing to slide.
  if (group.offsetWidth === 0 || to.offsetWidth === 0 || from.offsetWidth === 0) return;
  const a = markBox(kind, from, group);
  const b = markBox(kind, to, group);
  if (a.width <= 0 || b.width <= 0) return;
  const mark = document.createElement("span");
  mark.className = `slidemark slidemark--${kind}`;
  mark.setAttribute("aria-hidden", "true");
  group.appendChild(mark);
  to.classList.add("is-landing");
  const px = (box: typeof a) => ({ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` });
  const anim = mark.animate([px(a), px(b)], { duration: 220, easing: EASE_OUT, fill: "forwards" });
  running.set(group, anim);
  const done = () => {
    mark.remove();
    to.classList.remove("is-landing");
    if (running.get(group) === anim) running.delete(group);
  };
  anim.onfinish = done;
  anim.oncancel = done;
  // A background tab freezes animations; the selection must never stay unpainted.
  setTimeout(done, 600);
}

/**
 * Watches the class attribute of every tab, toggle and nav link in the app.
 * When "is-on" moves from one item to another in the same group, a mark slides
 * between them instead of the selection jumping. Installed once, from main.tsx.
 */
export function installSliders(): void {
  if (typeof MutationObserver === "undefined") return;
  const itemSel = SLIDERS.map((s) => s.item).join(",");
  const observer = new MutationObserver((records) => {
    if (reducedMotion()) return;
    const lost = new Map<Element, HTMLElement>();
    const gained = new Map<Element, HTMLElement>();
    for (const rec of records) {
      const el = rec.target as HTMLElement;
      if (!(el instanceof HTMLElement) || !el.matches(itemSel)) continue;
      const was = hasOn(rec.oldValue);
      const is = el.classList.contains("is-on");
      if (was === is) continue;
      const spec = SLIDERS.find((s) => el.matches(s.item));
      const group = spec && el.closest(spec.group);
      if (!group) continue;
      (is ? gained : lost).set(group, el);
    }
    for (const [group, to] of gained) {
      const from = lost.get(group);
      const spec = SLIDERS.find((s) => to.matches(s.item));
      if (from && spec && from.isConnected && to.isConnected) slide(spec.kind, group as HTMLElement, from, to);
    }
  });
  observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["class"], attributeOldValue: true });
}

/* ------------------------------------------------------------------ *
 * Ghosts: a still copy of something React just removed, animated out
 * ------------------------------------------------------------------ */

/**
 * Inline styles are copied with the CSSOM, not as attributes: the CSP has no
 * 'unsafe-inline', so a cloned style="" attribute may be dropped. That would
 * lose a team modal's club colours, which are custom properties set inline.
 */
function copyInlineStyles(src: HTMLElement, dst: HTMLElement) {
  const a = [src, ...src.querySelectorAll<HTMLElement>("[style]")];
  const b = [dst, ...dst.querySelectorAll<HTMLElement>("[style]")];
  if (a.length !== b.length) return;
  a.forEach((el, i) => { b[i].style.cssText = el.style.cssText; });
}

/** The open dialog a ghost must join to be seen (the top layer covers everything else). */
function topHost(except?: Element | null): Element {
  const open = [...document.querySelectorAll("dialog[open]")].filter((d) => d !== except && d.isConnected);
  return open[open.length - 1] ?? document.querySelector(".app") ?? document.body;
}

/**
 * React removes a component's DOM right after its layout-effect cleanup runs.
 * StrictMode's rehearsal runs the same cleanup and keeps the DOM, so a ghost is
 * only shown once a microtask confirms the original really is gone.
 */
function whenRemoved(el: Element, show: () => void) {
  queueMicrotask(() => { if (!el.isConnected) show(); });
}

/* ---- 6. modals: closing ---- */

const phoneQuery = typeof window !== "undefined" ? window.matchMedia("(max-width: 560px)") : null;

/**
 * Call from a modal's layout-effect cleanup, while the dialog is still on the
 * page. Leaves a copy of the panel and its backdrop that fades out (desktop) or
 * slides down (phone), so closing doesn't cut to the page in one frame.
 */
export function modalExit(dialog: HTMLDialogElement | null): void {
  if (!dialog || !dialog.open || reducedMotion()) return;
  const panel = dialog.querySelector<HTMLElement>(":scope > .modal__panel");
  if (!panel) return;
  const scroll = panel.scrollTop;
  const clone = panel.cloneNode(true) as HTMLElement;
  copyInlineStyles(panel, clone);
  clone.removeAttribute("id");
  const dialogClass = dialog.className;
  // A sheet dragged down (DSPRT-611) leaves from where the finger let go.
  const dragged = panel.style.transform;
  const fade = dragged ? Math.max(0, Math.min(1, parseFloat(dialog.style.getPropertyValue("--sheet-fade")) || 0)) : 1;
  if (!(dialog.matches(".sv") && superViewClose(dialog, clone))) heroClose(dialog, clone);
  returnToOpener(dialog);
  whenRemoved(dialog, () => {
    const ghost = document.createElement("div");
    ghost.className = `${dialogClass} modal-ghost`;
    ghost.setAttribute("aria-hidden", "true");
    ghost.inert = true;
    ghost.appendChild(clone);
    topHost(dialog).appendChild(ghost);
    clone.scrollTop = scroll;
    const phone = !!phoneQuery?.matches;
    const ms = phone ? 220 : 150;
    const anim = phone
      ? clone.animate([{ transform: dragged || "none" }, { transform: "translateY(100%)" }], { duration: ms, easing: EASE_IN, fill: "forwards" })
      : clone.animate([{ opacity: 1, transform: "none" }, { opacity: 0, transform: "scale(.98)" }], { duration: ms, easing: EASE_IN, fill: "forwards" });
    ghost.animate([{ backgroundColor: fadeAlpha(getComputedStyle(ghost).backgroundColor, fade) }, { backgroundColor: "rgba(0, 0, 0, 0)" }], { duration: ms, easing: EASE_IN, fill: "forwards" });
    const end = () => ghost.remove();
    anim.onfinish = end;
    anim.oncancel = end;
    // Also on a timer (GL-13): where animations don't run (a hidden or throttled
    // page) finish never fires, and the copy stayed in the next dialog, an empty
    // club card drawn over "Team not found".
    window.setTimeout(end, ms + 250);
  });
}

/** An rgba() colour with its alpha scaled by k: the backdrop as faded as a drag had left it. */
function fadeAlpha(color: string, k: number): string {
  if (k >= 1) return color;
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) return color;
  const [r, g, b, a = "1"] = m[1].split(",").map((x) => x.trim());
  return `rgba(${r}, ${g}, ${b}, ${(parseFloat(a) * k).toFixed(3)})`;
}

/* ---- 4. the loader finishes the climb ---- */

/** The first ancestor with a solid background, so a ghost can cover what arrives under it. */
function groundOf(el: Element): string {
  for (let n: Element | null = el.parentElement; n; n = n.parentElement) {
    const bg = getComputedStyle(n).backgroundColor;
    if (bg && bg !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(bg)) return bg;
  }
  return "";
}

/**
 * Call from Loading's layout-effect cleanup. When a loader that was actually on
 * screen goes away, a copy of it stays for a moment: the ridge completes from
 * wherever its loop was, the flag goes up, then the copy fades and uncovers
 * the content that just arrived. Inside a .skelwrap, the skeleton is copied as
 * well, and its tiles fade out left to right, as if the content sweeps in.
 */
export function loaderFinish(loader: HTMLElement | null, shownSince: number): void {
  if (!loader || reducedMotion()) return;
  // Inside a dialog the content is already there to read: a ghost, with its
  // ground and skeleton, would sit over it for up to a second after it landed.
  if (loader.closest("dialog")) return;
  // The loader is hidden for its first 150ms (base.css), so a fast read never showed it.
  if (performance.now() - shownSince < 160) return;
  const wrap = loader.closest<HTMLElement>(".skelwrap");
  const target = wrap ?? loader;
  const rect = target.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;
  const ridge = loader.querySelector(".ridgeload__ridge");
  const flag = loader.querySelector(".ridgeload__flag");
  if (!ridge || !flag) return;
  const rs = getComputedStyle(ridge);
  const fs = getComputedStyle(flag);
  const from = {
    off: rs.strokeDashoffset, rOp: rs.opacity,
    fT: fs.transform === "none" ? "scaleY(1)" : fs.transform, fOp: fs.opacity,
  };
  const ground = wrap ? "" : groundOf(loader);
  const clone = target.cloneNode(true) as HTMLElement;
  copyInlineStyles(target, clone);
  const host = loader.closest("dialog") ?? document.querySelector(".app") ?? document.body;

  whenRemoved(target, () => {
    const box = document.createElement("div");
    box.className = "loadghost";
    box.setAttribute("aria-hidden", "true");
    Object.assign(box.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
    if (ground) box.style.background = ground;
    clone.style.margin = "0";
    clone.removeAttribute("role");
    box.appendChild(clone);
    host.appendChild(box);

    const svg = clone.querySelector(".ridgeload");
    const cRidge = clone.querySelector(".ridgeload__ridge");
    const cFlag = clone.querySelector(".ridgeload__flag");
    svg?.classList.add("ridgeload--done");
    cRidge?.animate([{ strokeDashoffset: from.off, opacity: from.rOp }, { strokeDashoffset: "0", opacity: 1 }], { duration: 150, easing: EASE_OUT });
    cFlag?.animate([{ transform: from.fT, opacity: from.fOp }, { transform: "scaleY(1)", opacity: 1 }], { duration: 350, delay: 80, easing: EASE_SPRING, fill: "backwards" });

    let last: Animation;
    if (wrap) {
      const tiles = [...clone.querySelectorAll<HTMLElement>(".skel > *, .ldr--skel > *")];
      const skel = clone.querySelector<HTMLElement>(".skel, .ldr--skel");
      if (skel) skel.style.background = "transparent";
      tiles.forEach((t, i) => t.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, delay: 100 + Math.min(i, 12) * 35, easing: EASE_OUT, fill: "forwards" }));
      const plate = clone.querySelector<HTMLElement>(".loading");
      last = (plate ?? clone).animate([{ opacity: 1, transform: "none" }, { opacity: 0, transform: "scale(.96)" }], { duration: 180, delay: 380, easing: EASE_IN, fill: "forwards" });
      const tilesEnd = 100 + Math.min(tiles.length - 1, 12) * 35 + 220;
      if (tilesEnd > 560) last = clone.animate([{ opacity: 1 }, { opacity: 1 }], { duration: tilesEnd });
    } else {
      last = box.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, delay: 300, easing: EASE_IN, fill: "forwards" });
    }
    const end = () => box.remove();
    last.onfinish = end;
    last.oncancel = end;
  });
}

/* ------------------------------------------------------------------ *
 * 7. Rows slide to their new rank
 * ------------------------------------------------------------------ */

/**
 * For a list whose rows can change order (standings after a sort or a night of
 * results). Rows carry data-flip="<stable key>". After a render that moves a
 * row, it slides from where it was to where it is now. Rows that are new to
 * the list just appear (the .rise fade handles those).
 */
export function useFlip<T extends HTMLElement>(order: string): RefObject<T> {
  const ref = useRef<T>(null);
  const prev = useRef<Map<string, [number, number]>>(new Map());
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const rows = [...root.querySelectorAll<HTMLElement>(":scope > [data-flip]")];
    // Left as well as top (DSPRT-611): a grid of cards moves sideways too.
    const now = new Map(rows.map((r) => [r.dataset.flip ?? "", [r.offsetLeft, r.offsetTop] as [number, number]]));
    // A wholesale re-layout (a filter swapping most of a long list) isn't a
    // move worth showing; only a handful of rows shifting is.
    const moved = (r: HTMLElement) => {
      const was = prev.current.get(r.dataset.flip ?? "");
      return was != null && (Math.abs(was[0] - r.offsetLeft) > 1 || Math.abs(was[1] - r.offsetTop) > 1);
    };
    const moving = rows.filter(moved);
    if (!reducedMotion() && prev.current.size && moving.length <= 40) {
      moving.forEach((r) => {
        const was = prev.current.get(r.dataset.flip ?? "")!;
        const dx = was[0] - r.offsetLeft, dy = was[1] - r.offsetTop;
        r.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], { duration: 400, easing: EASE_IN_OUT });
      });
    }
    prev.current = now;
  }, [order]);
  return ref;
}

/* ------------------------------------------------------------------ *
 * 2. The headshot (or crest) you tap travels into the profile
 * ------------------------------------------------------------------ */

let lastPress: { el: Element; t: number } | null = null;
const heroSource = new WeakMap<Element, HTMLImageElement>();

/** Remembers what was last clicked, so a profile opening next knows where it came from. */
export function installHeroTracking(): void {
  document.addEventListener("click", (e) => {
    if (e.target instanceof Element) lastPress = { el: e.target, t: performance.now() };
  }, true);
}

/**
 * The one image of this kind beside what was clicked: walks up from the click
 * until a container holds exactly one, and gives up once one holds several
 * (a whole list), since then it can't tell which row was meant.
 */
function imageNear(el: Element, sel: string): HTMLImageElement | null {
  let n: Element | null = el;
  for (let depth = 0; n && depth < 6; depth++, n = n.parentElement) {
    if (n.matches("dialog, main, .app, body")) return null;
    const found = n.matches(sel) ? [n] : [...n.querySelectorAll(sel)];
    if (found.length > 1) return null;
    const img = found[0] as HTMLImageElement | undefined;
    if (img && img.complete && img.naturalWidth > 0) {
      const r = img.getBoundingClientRect();
      if (r.width > 0 && r.bottom > 0 && r.top < innerHeight) return img;
      return null;
    }
  }
  return null;
}

/** Undoes the panel's entrance transform, so the flight lands where the image will rest. */
function restingRect(target: Element, panel: Element | null): DOMRect {
  const r = target.getBoundingClientRect();
  if (!panel) return r;
  const t = getComputedStyle(panel).transform;
  if (!t || t === "none") return r;
  const m = new DOMMatrixReadOnly(t);
  const p = panel.getBoundingClientRect();
  const cx = p.left + p.width / 2, cy = p.top + p.height / 2;
  const s = m.a || 1;
  // Screen = origin + s * (rest - origin) + translate, where origin + translate is the panel's centre.
  return new DOMRect(cx - m.e + (r.left - cx) / s, cy - m.f + (r.top - cy) / s, r.width / s, r.height / s);
}

function fly(src: DOMRect, dst: DOMRect, url: string, host: Element, cls: string): Animation {
  const ghost = document.createElement("img");
  ghost.src = url;
  ghost.alt = "";
  ghost.className = `heroghost ${cls}`;
  ghost.setAttribute("aria-hidden", "true");
  Object.assign(ghost.style, { left: `${dst.left}px`, top: `${dst.top}px`, width: `${dst.width}px`, height: `${dst.height}px` });
  host.appendChild(ghost);
  const from = `translate(${src.left - dst.left}px, ${src.top - dst.top}px) scale(${src.width / dst.width}, ${src.height / dst.height})`;
  const anim = ghost.animate([{ transform: from }, { transform: "none" }], { duration: 400, easing: EASE_IN_OUT, fill: "forwards" });
  const end = () => ghost.remove();
  anim.onfinish = end;
  anim.oncancel = end;
  return anim;
}

const HERO_TARGETS: ReadonlyArray<{ target: string; source: string; cls: string }> = [
  { target: ".modal__head img.ph__shot", source: "img.ph__shot", cls: "heroghost--shot" },
  { target: ".modal__head .tm__id > img.crest", source: "img.crest", cls: "heroghost--crest" },
];

/**
 * Called as a modal opens. If it was opened by a click right beside a
 * headshot or crest, a copy of that image flies from the click to the
 * profile's header while the panel scales in, and the header image shows
 * once it lands.
 */
export function heroOpen(dialog: HTMLDialogElement): void {
  const press = lastPress;
  lastPress = null;
  rememberOpener(dialog, press);
  if (dialog.matches(".sv")) { superViewOpen(dialog); return; }
  if (!press || performance.now() - press.t > 1000 || reducedMotion()) return;
  for (const h of HERO_TARGETS) {
    const target = dialog.querySelector<HTMLImageElement>(h.target);
    if (!target) continue;
    const src = imageNear(press.el, h.source);
    if (!src) return;
    const a = src.getBoundingClientRect();
    const b = restingRect(target, dialog.querySelector(".modal__panel"));
    if (b.width === 0) return;
    target.style.visibility = "hidden";
    const anim = fly(a, b, src.currentSrc || src.src, dialog, h.cls);
    const show = () => { target.style.visibility = ""; };
    anim.addEventListener("finish", show);
    anim.addEventListener("cancel", show);
    setTimeout(show, 700);
    heroSource.set(dialog, src);
    return;
  }
}

/** The way back: as a profile closes, its header image flies home to where it came from. */
function heroClose(dialog: HTMLDialogElement, panelClone: HTMLElement): void {
  const src = heroSource.get(dialog);
  if (!src) return;
  for (const h of HERO_TARGETS) {
    const shot = dialog.querySelector<HTMLImageElement>(h.target);
    if (!shot) continue;
    const a = shot.getBoundingClientRect();
    const cloneShot = panelClone.querySelector<HTMLImageElement>(h.target.replace(".modal__head ", ""));
    queueMicrotask(() => {
      if (dialog.isConnected || !src.isConnected) return;
      // Only when nothing is left open on top of where it lands.
      if (document.querySelector("dialog[open]")) return;
      const b = src.getBoundingClientRect();
      if (b.width === 0 || b.bottom < 0 || b.top > innerHeight) return;
      if (cloneShot) cloneShot.style.visibility = "hidden";
      src.style.visibility = "hidden";
      const anim = fly(b, a, shot.currentSrc || shot.src, document.querySelector(".app") ?? document.body, h.cls);
      // Flown the other way: from the header back down to the row.
      anim.effect && (anim.effect as KeyframeEffect).setKeyframes([
        { transform: "none" },
        { transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${b.width / a.width}, ${b.height / a.height})` },
      ]);
      const show = () => { src.style.visibility = ""; };
      anim.addEventListener("finish", show);
      anim.addEventListener("cancel", show);
      setTimeout(show, 700);
    });
    return;
  }
}

/* ------------------------------------------------------------------ *
 * 10. Long pages reveal as you scroll · 11. Signature moments
 * ------------------------------------------------------------------ */

type RevealKind = "rise" | "flag" | "banner" | "belt" | "result";
const REVEALS: ReadonlyArray<{ sel: string; kind: RevealKind }> = [
  // 10: the long browse pages, and the sections further down a profile.
  { sel: ".chp-row", kind: "rise" },
  { sel: ".ldr__card", kind: "rise" },
  { sel: ".tm__sec", kind: "rise" },
  // 11: once each, the first time it is seen.
  { sel: ".summitflag", kind: "flag" },
  { sel: ".ttl-ban", kind: "banner" },
  { sel: ".ufc-belt", kind: "belt" },
  // DSPRT-203: a finished bout plays its result once, in the winner's corner.
  { sel: ".ufc-bout:has(.ufc-w)", kind: "result" },
];
const seen = new WeakSet<Element>();

/**
 * Plays an entrance the first time an element scrolls into view, and never
 * again. A row or section that is already on screen when it appears is left
 * alone: nothing above the fold waits for an observer. The signature marks
 * (the summit flag, a title banner, a champion's belt) play even there,
 * because they're small and the moment is the point. Elements that aren't
 * laid out yet (inside something hidden) are skipped and simply show.
 */
export function installReveals(): void {
  if (typeof IntersectionObserver === "undefined" || typeof MutationObserver === "undefined") return;
  let batch = 0;
  let batchTimer = 0;
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const el = e.target as HTMLElement;
      io.unobserve(el);
      // Several arriving together step in 40ms apart.
      el.style.setProperty("--rv-delay", `${Math.min(batch++, 8) * 40}ms`);
      el.classList.remove("rv-wait");
      el.classList.add("rv-in");
      const done = (ev: AnimationEvent) => {
        // A banner runs two animations; it's done when the swing ends.
        if (ev.target !== el || !/^rv-(rise|flag|swing|belt|result)$/.test(ev.animationName)) return;
        el.removeEventListener("animationend", done);
        el.classList.remove("rv-in");
        el.style.removeProperty("--rv-delay");
      };
      el.addEventListener("animationend", done);
    }
    clearTimeout(batchTimer);
    batchTimer = window.setTimeout(() => { batch = 0; }, 120);
  }, { rootMargin: "0px 0px -6% 0px" });

  const consider = (el: HTMLElement, kind: RevealKind) => {
    // [data-norv]: something that plays its own entrance (the bracket's champion).
    if (seen.has(el) || el.closest(".modal-ghost, .loadghost, [data-norv]")) return;
    seen.add(el);
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    if (kind === "rise" && r.top < innerHeight && r.bottom > 0) return;
    el.dataset.rv = kind;
    // A result stays readable while it waits; the sweep plays over it.
    if (kind !== "result") el.classList.add("rv-wait");
    io.observe(el);
  };

  const scan = (root: Element) => {
    // 7 (DSPRT-203): something just added to a trade flies over from the row it was added from.
    if (root.matches(".tr-item")) tradeFly(root as HTMLElement);
    else root.querySelectorAll<HTMLElement>(".tr-item").forEach(tradeFly);
    for (const { sel, kind } of REVEALS) {
      try {
        if (root.matches(sel)) consider(root as HTMLElement, kind);
        root.querySelectorAll<HTMLElement>(sel).forEach((el) => consider(el, kind));
      } catch { /* a browser without :has() skips that entry */ }
    }
  };

  new MutationObserver((records) => {
    if (reducedMotion()) return;
    for (const rec of records) rec.addedNodes.forEach((n) => { if (n instanceof HTMLElement || n instanceof SVGElement) scan(n as Element); });
  }).observe(document.body, { subtree: true, childList: true });

  // Nothing may stay hidden on paper.
  addEventListener("beforeprint", () => document.querySelectorAll(".rv-wait").forEach((el) => el.classList.remove("rv-wait")));
}

/**
 * Ids first seen after the initial render (DSPRT-203): the plays and shots
 * that arrived while you were watching, as opposed to the ones that were
 * already there when the feed opened. Only those animate in.
 */
export function useArrivals(ids: string[]): (id: string) => boolean {
  const seen = useRef<Set<string> | null>(null);
  const fresh = useRef<Set<string>>(new Set());
  if (seen.current) {
    for (const id of ids) if (!seen.current.has(id)) { fresh.current.add(id); seen.current.add(id); }
  }
  useLayoutEffect(() => {
    if (!seen.current) seen.current = new Set(ids);
  });
  return (id) => fresh.current.has(id) && !reducedMotion();
}

/* ------------------------------------------------------------------ *
 * 9. Alerts spring in, stack and swipe away (DSPRT-203)
 * ------------------------------------------------------------------ */

/**
 * Call from a toast's layout-effect cleanup. A copy of it leaves the way it
 * was swiped (or to the right, when it timed out or was closed), from
 * wherever a drag had left it.
 */
export function leaveGhost(el: HTMLElement | null, dir: number): void {
  if (!el || reducedMotion()) return;
  const r = el.getBoundingClientRect();
  if (r.width === 0) return;
  const clone = el.cloneNode(true) as HTMLElement;
  copyInlineStyles(el, clone);
  // A drag's offset is already in the measured rect, so the flight starts there.
  const opacity = el.style.opacity || "1";
  whenRemoved(el, () => {
    clone.classList.add("favalert-ghost");
    clone.removeAttribute("data-flip");
    clone.setAttribute("aria-hidden", "true");
    Object.assign(clone.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, transform: "none" });
    (document.querySelector(".app") ?? document.body).appendChild(clone);
    const a = clone.animate(
      [{ transform: "none", opacity: Number(opacity) }, { transform: `translateX(${dir < 0 ? -115 : 115}%)`, opacity: 0 }],
      { duration: 200, easing: EASE_IN, fill: "forwards" },
    );
    const end = () => clone.remove();
    a.onfinish = end;
    a.oncancel = end;
  });
}

/* ------------------------------------------------------------------ *
 * 7. Players fly across the deal (DSPRT-203)
 * ------------------------------------------------------------------ */

/**
 * A player or pick that lands in the deal panel right after a click in a
 * roster flies there from the row that was clicked. The real item stays
 * hidden until the copy lands on it.
 */
function tradeFly(item: HTMLElement): void {
  const press = lastPress;
  if (!press || performance.now() - press.t > 800 || reducedMotion()) return;
  const src = press.el.closest<HTMLElement>(".tr-p, .tr-pick");
  if (!src || !src.isConnected) return;
  lastPress = null;
  const a = src.getBoundingClientRect(), b = item.getBoundingClientRect();
  if (!a.width || !b.width || b.bottom < 0 || b.top > innerHeight) return;
  const clone = item.cloneNode(true) as HTMLElement;
  copyInlineStyles(item, clone);
  clone.classList.add("tr-flyghost");
  clone.setAttribute("aria-hidden", "true");
  Object.assign(clone.style, { left: `${b.left}px`, top: `${b.top}px`, width: `${b.width}px`, height: `${b.height}px` });
  (document.querySelector(".app") ?? document.body).appendChild(clone);
  item.style.visibility = "hidden";
  const anim = clone.animate(
    [{ transform: `translate(${a.left - b.left}px, ${a.top - b.top}px)`, opacity: .5 }, { transform: "none", opacity: 1 }],
    { duration: 400, easing: EASE_IN_OUT, fill: "forwards" },
  );
  const end = () => { clone.remove(); item.style.visibility = ""; };
  anim.onfinish = end;
  anim.oncancel = end;
  setTimeout(end, 700);
}

/* ------------------------------------------------------------------ *
 * 10. A box that changes height eases to its new size
 * ------------------------------------------------------------------ */

/**
 * Runs `update` (a state change that makes `el` taller or shorter) and eases
 * the box from its old height to its new one, clipping what hasn't been
 * revealed yet. React commits inside the call so the new height can be
 * measured; a click in the middle of a move starts from where the box is.
 */
export function heightShift(el: HTMLElement | null, update: () => void): void {
  if (!el || reducedMotion()) { update(); return; }
  // Layout pixels, not the on-screen rect: a modal mid-entrance is scaled.
  const px = () => parseFloat(getComputedStyle(el).height);
  const from = px();
  el.getAnimations().forEach((a) => a.cancel());
  flushSync(update);
  const to = px();
  if (Math.abs(from - to) < 1) return;
  const growing = to > from;
  el.style.overflow = "hidden";
  const a = el.animate([{ height: `${from}px` }, { height: `${to}px` }],
    { duration: growing ? 320 : 260, easing: growing ? EASE_OUT : EASE_IN_OUT });
  a.onfinish = () => { el.style.overflow = ""; };
}

/* ================================================================== *
 * Round 3 (DSPRT-611)
 * ================================================================== */

/* ------------------------------------------------------------------ *
 * 1. Sorted tables glide into place
 * ------------------------------------------------------------------ */

/**
 * A click on a sortable column header (th[aria-sort]) in any table: the rows
 * that change place slide from where they were to where the sort put them,
 * instead of the table re-rendering in one jump. Cells move rather than rows,
 * because a sticky first column inside a transformed row loses its place.
 * Tables whose rows already slide with useFlip (data-flip) are left to it.
 * Only rows that are or were on screen move; a long list's far rows just land.
 */
export function installSortGlide(): void {
  document.addEventListener("click", (e) => {
    if (reducedMotion() || !(e.target instanceof Element)) return;
    const th = e.target.closest("th[aria-sort]");
    const body = th?.closest("table")?.tBodies[0];
    if (!body || body.querySelector(":scope > [data-flip]")) return;
    const rows = [...body.rows];
    if (rows.length < 2 || rows.length > 600) return;
    const before = new Map(rows.map((r) => [r, r.getBoundingClientRect().top]));
    // React commits the sort before the next frame is painted.
    requestAnimationFrame(() => {
      const off = (y: number) => y > innerHeight || y < -60;
      let moved = 0;
      for (const r of body.rows) {
        const was = before.get(r);
        if (was == null) continue;
        const now = r.getBoundingClientRect().top;
        const dy = was - now;
        if (Math.abs(dy) < 1 || (off(was) && off(now))) continue;
        if (++moved > 80) break;
        for (const c of r.cells) c.animate([{ transform: `translateY(${dy}px)` }, { transform: "none" }], { duration: 400, easing: EASE_IN_OUT });
      }
    });
  }, true);
}

/* ------------------------------------------------------------------ *
 * 2. Leader boards re-rank when what they rank changes
 * ------------------------------------------------------------------ */

type BoardRow = { top: number; left: number; width: number; copy: HTMLElement };
const boardMemory = new Map<string, { at: number; list: Element; rows: Map<string, BoardRow> }>();

/**
 * For a ranked list that changes under the reader: another season, totals
 * instead of averages, another award race. Rows carry data-rank="<player>".
 * A player on both boards slides from his old place to his new one, a
 * newcomer fades in, and one who dropped off fades out where he stood. The
 * last order of each board (by name) is remembered for half a minute, so it
 * works when the board is drawn afresh after a load as well as in place.
 */
export function useRankShift<T extends HTMLElement>(board: string, order: string): RefObject<T> {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;
    const rows = [...list.querySelectorAll<HTMLElement>(":scope > [data-rank]")];
    // Layout offsets, not the screen: a row mid-way through its .rise fade is translated.
    const rel = (el: HTMLElement) => (el.offsetParent === list ? el.offsetTop : el.offsetTop - list.offsetTop);
    const prev = boardMemory.get(board);
    if (prev && performance.now() - prev.at < 30_000 && !reducedMotion() && document.visibilityState === "visible") {
      const keep = new Set(rows.map((r) => r.dataset.rank));
      let entering = 0;
      for (const r of rows) {
        const was = prev.rows.get(r.dataset.rank ?? "");
        if (!was) {
          r.animate([{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "none" }],
            { duration: 240, delay: 120 + Math.min(entering++, 8) * 25, easing: EASE_OUT, fill: "backwards" });
          continue;
        }
        const dy = was.top - rel(r);
        if (Math.abs(dy) > 1) r.animate([{ transform: `translateY(${dy}px)` }, { transform: "none" }], { duration: 400, easing: EASE_IN_OUT });
      }
      // Leavers fade where they stood only on a board that changed in place:
      // one drawn afresh after a loader had already lost them from view. A
      // table body can't hold a positioned copy at all.
      if (list.tagName !== "TBODY" && prev.list === list) {
        for (const [key, was] of prev.rows) {
          if (keep.has(key)) continue;
          const g = was.copy.cloneNode(true) as HTMLElement;
          copyInlineStyles(was.copy, g);
          g.classList.add("rankghost");
          g.setAttribute("aria-hidden", "true");
          g.inert = true;
          Object.assign(g.style, { left: `${was.left}px`, top: `${was.top}px`, width: `${was.width}px` });
          list.appendChild(g);
          const a = g.animate([{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateX(-12px)" }], { duration: 200, easing: EASE_IN, fill: "forwards" });
          const end = () => g.remove();
          a.onfinish = end;
          a.oncancel = end;
        }
      }
    }
    const snap = new Map<string, BoardRow>();
    for (const r of rows) {
      const copy = r.cloneNode(true) as HTMLElement;
      copyInlineStyles(r, copy);
      copy.removeAttribute("data-rank");
      snap.set(r.dataset.rank ?? "", { top: rel(r), left: r.offsetParent === list ? r.offsetLeft : r.offsetLeft - list.offsetLeft, width: r.offsetWidth, copy });
    }
    boardMemory.set(board, { at: performance.now(), list, rows: snap });
  }, [board, order]);
  return ref;
}

/* ------------------------------------------------------------------ *
 * 3. Back lands you on the row you came from
 * ------------------------------------------------------------------ */

/** What counts as "the row" around a click: a table row, a list item, a card. */
const ROW_SEL = "tr, li, article, .card, [data-flip], [data-rank]";
const openedFrom = new WeakMap<HTMLDialogElement, HTMLElement>();

function rowAround(el: Element | null | undefined): HTMLElement | null {
  const r = el?.closest<HTMLElement>(ROW_SEL) ?? null;
  // The site's own navigation isn't a place in a list.
  if (!r || r.closest("nav, header, .tabbar, .top, .strip")) return null;
  // A whole section isn't a row: nothing taller than a big card.
  return r.getBoundingClientRect().height <= 260 ? r : null;
}

/** A short wash on the row, so the eye finds its place again. */
export function washRow(row: HTMLElement | null): void {
  if (!row || !row.isConnected || reducedMotion()) return;
  row.classList.remove("is-return");
  void row.offsetWidth;
  row.classList.add("is-return");
  window.setTimeout(() => row.classList.remove("is-return"), 1500);
}

/** Called as a dialog opens: the row it was opened from, if a click on one opened it. */
function rememberOpener(dialog: HTMLDialogElement, press: { el: Element; t: number } | null): void {
  if (!press || performance.now() - press.t > 1000 || press.el.closest("dialog") === dialog) return;
  const row = rowAround(press.el);
  if (row) openedFrom.set(dialog, row);
}

/** Called once a dialog has gone: its row, when it is in view and nothing covers it, gets the wash. */
function returnToOpener(dialog: HTMLDialogElement): void {
  const row = openedFrom.get(dialog);
  if (!row) return;
  window.setTimeout(() => {
    if (!row.isConnected) return;
    const top = [...document.querySelectorAll("dialog[open]")].pop();
    if (top && row.closest("dialog") !== top) return;
    const r = row.getBoundingClientRect();
    if (r.bottom < 0 || r.top > innerHeight || r.height === 0) return;
    washRow(row);
  }, 160);
}

/** Where a row is, in words that survive the page being drawn afresh. */
export interface RowMark { tag: string; key: string | null; text: string }

/** The row last clicked, within the last second, as a mark (the router keeps it on a page change). */
export function pressedRow(): RowMark | null {
  const press = lastPress;
  if (!press || performance.now() - press.t > 1000 || press.el.closest("dialog")) return null;
  const row = rowAround(press.el);
  if (!row) return null;
  return { tag: row.tagName, key: row.dataset.flip ?? row.dataset.rank ?? null, text: (row.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 80) };
}

/** The row each address was left from (DSPRT-611), for Back to that address. */
const leftFrom = new Map<string, RowMark>();
const here = () => window.location.pathname + window.location.search;

/** A router moving to another page by a click: remember the row the click was on, against the address being left. */
export function noteLeaving(): void {
  const mark = pressedRow();
  if (mark) leftFrom.set(here(), mark);
}

/** A router arriving on another page by Back or Forward: the row this address was left from gets the wash. */
export function noteArriving(): void {
  const mark = leftFrom.get(here());
  if (!mark) return;
  leftFrom.delete(here());
  returnToRow(mark);
}

/**
 * Back to a page left by a click on one of its rows: once that row is drawn
 * again (the page may still be loading), it is brought into view if the
 * browser's own scroll didn't land on it, and gets the wash.
 */
export function returnToRow(mark: RowMark): void {
  const until = performance.now() + 4000;
  const find = () => {
    const rows = [...document.querySelectorAll<HTMLElement>(`.app ${mark.tag.toLowerCase()}`)].filter((r) => !r.closest("dialog, .modal-ghost"));
    const row = rows.find((r) => mark.key != null ? (r.dataset.flip ?? r.dataset.rank) === mark.key
      : (r.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 80) === mark.text);
    if (!row) {
      if (performance.now() < until) window.setTimeout(find, 120);
      return;
    }
    const r = row.getBoundingClientRect();
    if (r.top < 60 || r.bottom > innerHeight) row.scrollIntoView({ block: "center", behavior: reducedMotion() ? "auto" : "smooth" });
    washRow(row);
  };
  window.setTimeout(find, 220);
}

/* ------------------------------------------------------------------ *
 * 6. Tab panels slide in tab order
 * ------------------------------------------------------------------ */

/**
 * Switches a tab's panel: `update` swaps the content inside `el`. The old
 * content leaves toward the side you came from, the new one comes in from
 * the side you're moving to (dir +1 is a tab further along, -1 one back),
 * and the box eases to its new height (heightShift). `from` is where a swipe
 * left the panel, so the move carries on from the finger.
 */
export function panelSwap(el: HTMLElement | null, update: () => void, dir: number, from = 0): void {
  if (!el || reducedMotion() || !dir || el.offsetWidth === 0) {
    if (el) el.style.transform = "";
    update();
    return;
  }
  const r = el.getBoundingClientRect();
  const host = topHost();
  const clip = (host.matches("dialog") ? host.querySelector<HTMLElement>(":scope > .modal__panel") : null)?.getBoundingClientRect()
    ?? new DOMRect(0, 0, innerWidth, innerHeight);
  const box = { left: Math.max(r.left, clip.left), top: Math.max(r.top, clip.top), right: Math.min(r.right, clip.right), bottom: Math.min(r.bottom, clip.bottom) };
  const clone = el.cloneNode(true) as HTMLElement;
  copyInlineStyles(el, clone);
  clone.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
  el.style.transform = "";
  heightShift(el, update);
  if (box.right <= box.left || box.bottom <= box.top) return;
  const ghost = document.createElement("div");
  ghost.className = "panelghost";
  ghost.setAttribute("aria-hidden", "true");
  ghost.inert = true;
  Object.assign(ghost.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.right - box.left}px`, height: `${box.bottom - box.top}px` });
  Object.assign(clone.style, { position: "absolute", left: `${r.left - box.left}px`, top: `${r.top - box.top}px`, width: `${r.width}px`, margin: "0", transform: "none" });
  ghost.appendChild(clone);
  host.appendChild(ghost);
  const out = clone.animate([{ opacity: 1, transform: `translateX(${from}px)` }, { opacity: 0, transform: `translateX(${from - 32 * dir}px)` }],
    { duration: 160, easing: EASE_IN, fill: "forwards" });
  const end = () => ghost.remove();
  out.onfinish = end;
  out.oncancel = end;
  for (const c of el.children) {
    (c as HTMLElement).animate([{ opacity: 0, transform: `translateX(${32 * dir}px)` }, { opacity: 1, transform: "none" }],
      { duration: 240, delay: 40, easing: EASE_OUT, fill: "backwards" });
  }
}

/* ------------------------------------------------------------------ *
 * 9. Drag a phone's sheet down to close it
 * ------------------------------------------------------------------ */

/** Starting inside one of these is a gesture of its own, not a drag of the sheet. */
const NO_DRAG = "input, textarea, select, [contenteditable], .wp__plot, .sc__stage, .lc, .swb, [data-nodrag]";

/** A box between the touch and the panel that is scrolled down, or scrolls sideways, keeps the touch. */
function scrollsUnder(target: Element, panel: Element, axis: "x" | "y"): boolean {
  for (let n: Element | null = target; n && n !== panel; n = n.parentElement) {
    const s = getComputedStyle(n);
    if (axis === "y" && /(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight + 1 && n.scrollTop > 0) return true;
    if (axis === "x" && /(auto|scroll)/.test(s.overflowX) && n.scrollWidth > n.clientWidth + 2) return true;
  }
  return false;
}

/**
 * On a phone a dialog is a sheet that slid up from the bottom. Pulled down
 * from the top of its scroll, it follows the finger; let go past a third of
 * its height, or with a quick flick, it closes the way its Close button
 * would; otherwise it springs back. Installed once, from main.tsx.
 */
export function installSheetDrag(): void {
  let drag: { panel: HTMLElement; dialog: HTMLDialogElement; y0: number; x0: number; ly: number; lt: number; v: number; dy: number; on: boolean | null } | null = null;
  const phone = () => !!phoneQuery?.matches;
  document.addEventListener("touchstart", (e) => {
    drag = null;
    if (!phone() || reducedMotion() || e.touches.length !== 1 || !(e.target instanceof Element)) return;
    const dialog = e.target.closest<HTMLDialogElement>("dialog.modal[open]");
    const panel = dialog?.querySelector<HTMLElement>(":scope > .modal__panel");
    if (!dialog || !panel || !panel.contains(e.target) || panel.scrollTop > 0 || e.target.closest(NO_DRAG)) return;
    if (scrollsUnder(e.target, panel, "y")) return;
    const t = e.touches[0];
    drag = { panel, dialog, y0: t.clientY, x0: t.clientX, ly: t.clientY, lt: e.timeStamp, v: 0, dy: 0, on: null };
  }, { passive: true });
  document.addEventListener("touchmove", (e) => {
    if (!drag) return;
    const t = e.touches[0];
    const dy = t.clientY - drag.y0, dx = t.clientX - drag.x0;
    if (drag.on === null) {
      if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return;
      // Only a pull downwards that starts at the top of the sheet; anything else scrolls as usual.
      drag.on = dy > 0 && Math.abs(dy) > Math.abs(dx) * 1.2 && drag.panel.scrollTop <= 0;
      if (!drag.on) { drag = null; return; }
      drag.panel.getAnimations().forEach((a) => a.cancel());
    }
    e.preventDefault();
    const dt = e.timeStamp - drag.lt;
    if (dt > 0) { drag.v = (t.clientY - drag.ly) / dt; drag.ly = t.clientY; drag.lt = e.timeStamp; }
    drag.dy = Math.max(0, dy);
    drag.panel.style.transform = `translateY(${drag.dy}px)`;
    drag.dialog.style.setProperty("--sheet-fade", String(Math.max(0, 1 - drag.dy / drag.panel.offsetHeight)));
  }, { passive: false });
  const end = () => {
    const d = drag;
    drag = null;
    if (!d || !d.on) return;
    const h = d.panel.offsetHeight;
    if (d.dy > h / 3 || (d.v > 0.5 && d.dy > 24)) {
      // Closed as Close would close it; modalExit carries on from where the finger left it.
      const close = d.dialog.querySelector<HTMLElement>(".modal__close");
      if (close) { close.click(); return; }
      d.dialog.dispatchEvent(new Event("cancel", { cancelable: true }));
      return;
    }
    d.dialog.style.removeProperty("--sheet-fade");
    const from = d.panel.style.transform;
    d.panel.style.transform = "";
    d.panel.animate([{ transform: from }, { transform: "none" }], { duration: 300, easing: EASE_SPRING });
  };
  document.addEventListener("touchend", end, { passive: true });
  document.addEventListener("touchcancel", end, { passive: true });
}

/* ------------------------------------------------------------------ *
 * 10. Swipe between tabs on a phone
 * ------------------------------------------------------------------ */

/**
 * Drag a tab's panel sideways on a phone: it follows the finger and the tab
 * mark slides toward the tab you're heading for. Let go past a quarter of
 * the width, or with a flick, and `go` is called with the direction and the
 * offset the finger left it at (for panelSwap); otherwise it springs back.
 * A touch that starts in something that scrolls sideways (a wide box score)
 * stays that thing's.
 */
export function useSwipeTabs(
  panelRef: RefObject<HTMLElement>, tabsRef: RefObject<HTMLElement>,
  index: number, count: number, go: (dir: number, offset: number) => void,
): void {
  const state = useRef({ index, count, go });
  state.current = { index, count, go };
  // On the document, not the panel: the panel mounts later than this hook
  // (after the box score loads, when its fold opens).
  useEffect(() => {
    let panel: HTMLElement | null = null;
    let d: { x0: number; y0: number; lx: number; lt: number; v: number; dx: number; on: boolean | null; pull: HTMLElement | null } | null = null;
    const tabAt = (i: number) => tabsRef.current?.querySelectorAll<HTMLElement>("[role=tab]")[i] ?? null;
    const box = (el: HTMLElement) => {
      const g = tabsRef.current!.getBoundingClientRect(), r = el.getBoundingClientRect();
      return { left: r.left - g.left + tabsRef.current!.scrollLeft, width: r.width };
    };
    const start = (e: TouchEvent) => {
      d = null;
      panel = panelRef.current;
      if (!panel || !tabsRef.current || !phoneQuery?.matches || reducedMotion() || e.touches.length !== 1 || !(e.target instanceof Element)) return;
      if (!panel.contains(e.target) || e.target.closest(NO_DRAG) || scrollsUnder(e.target, panel, "x")) return;
      const t = e.touches[0];
      d = { x0: t.clientX, y0: t.clientY, lx: t.clientX, lt: e.timeStamp, v: 0, dx: 0, on: null, pull: null };
    };
    const move = (e: TouchEvent) => {
      if (!d || !panel) return;
      const t = e.touches[0];
      const dx = t.clientX - d.x0, dy = t.clientY - d.y0;
      if (d.on === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        d.on = Math.abs(dx) > Math.abs(dy) * 1.4;
        if (!d.on) { d = null; return; }
        const tabs = tabsRef.current, on = tabAt(state.current.index);
        if (tabs && on) {
          d.pull = document.createElement("span");
          d.pull.className = "tabpull";
          d.pull.setAttribute("aria-hidden", "true");
          tabs.appendChild(d.pull);
          tabs.classList.add("is-pulling");
        }
      }
      e.preventDefault();
      const { index: i, count: n } = state.current;
      const w = panel.offsetWidth || 1;
      const edge = (dx > 0 && i === 0) || (dx < 0 && i === n - 1);
      d.dx = edge ? dx * 0.3 : dx;
      const dt = e.timeStamp - d.lt;
      if (dt > 0) { d.v = (t.clientX - d.lx) / dt; d.lx = t.clientX; d.lt = e.timeStamp; }
      panel.style.transform = `translateX(${d.dx}px)`;
      const on = tabAt(i), next = tabAt(i + (d.dx < 0 ? 1 : -1));
      if (d.pull && on) {
        const a = box(on), b = next && !edge ? box(next) : a;
        const k = Math.min(1, Math.abs(d.dx) / w);
        Object.assign(d.pull.style, { left: `${a.left + (b.left - a.left) * k}px`, width: `${a.width + (b.width - a.width) * k}px` });
      }
    };
    const end = () => {
      const g = d;
      d = null;
      if (!g || !g.on || !panel) return;
      const tabs = tabsRef.current;
      const w = panel.offsetWidth || 1;
      const { index: i, count: n } = state.current;
      let dir = 0;
      if ((g.dx < -w / 4 || g.v < -0.4) && i < n - 1) dir = 1;
      else if ((g.dx > w / 4 || g.v > 0.4) && i > 0) dir = -1;
      const finish = () => { g.pull?.remove(); tabs?.classList.remove("is-pulling"); };
      if (dir) {
        const to = tabAt(i + dir);
        if (g.pull && to) {
          const b = box(to);
          const a = g.pull.animate([{ left: g.pull.style.left, width: g.pull.style.width }, { left: `${b.left}px`, width: `${b.width}px` }], { duration: 220, easing: EASE_OUT, fill: "forwards" });
          a.onfinish = finish;
          a.oncancel = finish;
          window.setTimeout(finish, 500);
        } else finish();
        state.current.go(dir, g.dx);
        return;
      }
      finish();
      const back = panel;
      const from = back.style.transform;
      back.style.transform = "";
      back.animate([{ transform: from }, { transform: "none" }], { duration: 300, easing: EASE_SPRING });
    };
    document.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end, { passive: true });
    document.addEventListener("touchcancel", end, { passive: true });
    return () => {
      document.removeEventListener("touchstart", start);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", end);
    };
  }, [panelRef, tabsRef]);
}

/* ------------------------------------------------------------------ *
 * 13. The live court grows into super-view
 * ------------------------------------------------------------------ */

/**
 * Called as super-view opens. The court in the game dialog under it grows
 * into super-view's court, while the bar and the two rails slide in. Both
 * courts are LiveCourt's drawing (.lc__court); the small one stays where it
 * was, underneath.
 */
export function superViewOpen(dialog: HTMLDialogElement): void {
  if (reducedMotion()) return;
  const big = dialog.querySelector<SVGSVGElement>(".lc__court");
  const small = [...document.querySelectorAll<SVGSVGElement>("dialog[open] .lc__court")].find((c) => !dialog.contains(c));
  if (!big || !small) return;
  const a = small.getBoundingClientRect(), b = big.getBoundingClientRect();
  if (!a.width || !b.width || a.bottom < 0 || a.top > innerHeight) return;
  big.style.transformOrigin = "0 0";
  const anim = big.animate([
    { transform: `translate(${a.left - b.left}px, ${a.top - b.top}px) scale(${a.width / b.width}, ${a.height / b.height})` },
    { transform: "none" },
  ], { duration: 400, easing: EASE_OUT });
  const done = () => { big.style.transformOrigin = ""; };
  anim.onfinish = done;
  anim.oncancel = done;
  const panel = dialog.querySelector<HTMLElement>(":scope > .modal__panel");
  // The court travels; everything else arrives around it.
  panel?.querySelectorAll<HTMLElement>(".sv__top, .sv__rail").forEach((n) => {
    const side = n.matches(".sv__rail") ? (n.matches(".sv__rail--home") ? 16 : -16) : 0;
    n.animate([{ opacity: 0, transform: side ? `translateX(${side}px)` : "translateY(-10px)" }, { opacity: 1, transform: "none" }],
      { duration: 240, delay: 180, easing: EASE_OUT, fill: "backwards" });
  });
}

/** Called as super-view closes, before the dialog goes: its court shrinks back into the game dialog's. */
function superViewClose(dialog: HTMLDialogElement, clone: HTMLElement): boolean {
  const big = dialog.querySelector<SVGSVGElement>(".lc__court");
  const small = [...document.querySelectorAll<SVGSVGElement>("dialog[open] .lc__court")].find((c) => !dialog.contains(c));
  if (!big || !small) return false;
  const a = big.getBoundingClientRect();
  const bigCopy = clone.querySelector<SVGSVGElement>(".lc__court");
  whenRemoved(dialog, () => {
    const b = small.getBoundingClientRect();
    if (!b.width || !bigCopy) return;
    const ghost = big.cloneNode(true) as SVGSVGElement;
    ghost.classList.add("courtghost");
    ghost.setAttribute("aria-hidden", "true");
    Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px`, transformOrigin: "0 0" });
    topHost(dialog).appendChild(ghost);
    (bigCopy as unknown as HTMLElement).style.visibility = "hidden";
    const anim = ghost.animate([{ transform: "none" }, { transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${b.width / a.width}, ${b.height / a.height})` }],
      { duration: 360, easing: EASE_IN_OUT, fill: "forwards" });
    const end = () => ghost.remove();
    anim.onfinish = end;
    anim.oncancel = end;
  });
  return true;
}

/* ------------------------------------------------------------------ *
 * 11. A live box score's scorer ticks up
 * ------------------------------------------------------------------ */

/**
 * In a live box score, a player whose points went up since the last reading
 * gets a short wash on his row and a "+2" that rises off his points cell.
 * Rows carry data-pid and data-pts; `game` resets the memory for another game.
 */
export function usePointTicks(rootRef: RefObject<HTMLElement>, live: boolean, game: string | null): void {
  const prev = useRef<{ game: string | null; pts: Map<string, number> }>({ game: null, pts: new Map() });
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (prev.current.game !== game) prev.current = { game, pts: new Map() };
    const seen = prev.current.pts;
    root.querySelectorAll<HTMLTableRowElement>("tr[data-pid]").forEach((tr) => {
      const id = tr.dataset.pid ?? "", n = Number(tr.dataset.pts);
      if (!id || !Number.isFinite(n)) return;
      const was = seen.get(id);
      seen.set(id, n);
      if (!live || was == null || n <= was || reducedMotion()) return;
      tr.classList.remove("is-scored");
      void tr.offsetWidth;
      tr.classList.add("is-scored");
      window.setTimeout(() => tr.classList.remove("is-scored"), 950);
      const cell = tr.querySelector<HTMLElement>(".bs__pts");
      if (!cell) return;
      const plus = document.createElement("span");
      plus.className = "bs__plus";
      plus.setAttribute("aria-hidden", "true");
      plus.textContent = `+${n - was}`;
      cell.appendChild(plus);
      window.setTimeout(() => plus.remove(), 950);
    });
  });
}
